import { AbstractControl } from "@angular/forms";
import { combineLatest, map, merge, Observable, startWith } from "rxjs";
import { AppDialogs } from "../../enums";
import { canRaiseCappedAmount } from "../../modals/capped-amount-warning/capped-amount-warning.interface";
import { canReceivePush } from "../../modals/send-push/send-push.interface";
import { DialogRegistry } from "./dialog.interface";

/** Whether a form (or control) is valid now and after every change. */
const formValid$ = (form: AbstractControl): Observable<boolean> =>
  form.statusChanges.pipe(
    startWith(null),
    map(() => form.valid),
  );

/** Re-reads a dialog's state now and after every change of its sources. */
const stateOf$ = <T>(sources: Observable<unknown>[], state: () => T): Observable<T> =>
  merge(...sources).pipe(
    startWith(null),
    map(() => state()),
  );

/**
 * Dialogs that open through `DialogService`: a Shopify modal inside the admin, a MatDialog outside it.
 * Both render the same component; only the title and the buttons move to Shopify's modal chrome.
 */
export const DIALOGS: DialogRegistry = {
  [AppDialogs.understood]: {
    load: () =>
      import("../../modals/understood/understood.module").then((m) => ({
        component: m.UnderstoodComponent,
        module: m.UnderstoodComponentModule,
      })),
    size: "small",
    heading: (data) => data.title,
    primaryAction: { label: () => "UNDERSTOOD_PAGE.button", run: (component) => component.understood() },
  },
  [AppDialogs.updateIntegrationConfirm]: {
    load: () =>
      import("../../modals/update-integration-confirm/update-integration-confirm.module").then((m) => ({
        component: m.UpdateIntegrationConfirmComponent,
        module: m.UpdateIntegrationConfirmComponentModule,
      })),
    size: "small",
    heading: () => "UPDATE_INTEGRATION_CONFIRM.title",
    primaryAction: {
      label: () => "UPDATE_INTEGRATION_CONFIRM.confirm",
      tone: () => "critical",
      run: (component) => component.confirm(true),
    },
    secondaryAction: { label: () => "UPDATE_INTEGRATION_CONFIRM.cancel", run: (component) => component.confirm(false) },
  },
  [AppDialogs.autoTranslateConfirm]: {
    load: () =>
      import("../../modals/auto-translate-confirm/auto-translate-confirm.module").then((m) => ({
        component: m.AutoTranslateConfirmComponent,
        module: m.AutoTranslateConfirmModule,
      })),
    size: "base",
    heading: () => "AUTO_TRANSLATE_CONFIRM.title",
    primaryAction: { label: () => "AUTO_TRANSLATE_CONFIRM.confirm", run: (component) => component.confirm() },
    secondaryAction: { label: () => "AUTO_TRANSLATE_CONFIRM.skip", run: (component) => component.skip() },
  },
  [AppDialogs.redemptionType]: {
    load: () =>
      import("../../modals/redemption-type/redemption-type.module").then((m) => ({
        component: m.RedemptionTypeComponent,
        module: m.RedemptionTypeComponentModule,
      })),
    size: "base",
    heading: () => "REDEMPTION_TYPE_DIALOG.title",
    matDialogConfig: { autoFocus: "dialog" },
  },
  [AppDialogs.shopifyMultiPass]: {
    load: () =>
      import("../../modals/shopify-multi-pass/shopify-multi-pass.module").then((m) => ({
        component: m.ShopifyMultiPassComponent,
        module: m.ShopifyMultiPassComponentModule,
      })),
    size: "base",
    heading: () => "COMPONENTS.SHOPIFY_MULTI_PASS.title",
    primaryAction: { label: () => "COMPONENTS.SHOPIFY_MULTI_PASS.save", run: (component) => component.save() },
    secondaryAction: { label: () => "COMPONENTS.SHOPIFY_MULTI_PASS.cancel", run: (component) => component.dialogRef.close() },
  },
  [AppDialogs.videoPlayer]: {
    load: () =>
      import("../../modals/video-player/video-player.module").then((m) => ({
        component: m.VideoPlayerComponent,
        module: m.VideoPlayerComponentModule,
      })),
    size: "max",
    width: "calc(100vw - 2 * var(--space-L))",
    heading: (data) => data.title,
    matDialogConfig: {
      panelClass: "video-player-dialog",
      height: "calc(100vh - 2 * var(--space-L) - var(--page-bottom-safe-area))",
      maxWidth: "none",
      position: { top: "var(--space-L)" },
    },
  },

  [AppDialogs.addLocation]: {
    load: () =>
      import("../../modals/add-location/add-location.module").then(m => ({
        component: m.AddLocationComponent,
        module: m.AddLocationComponentModule,
      })),
    size: "base",
    heading: () => "COMPONENTS.ADD_LOCATION.header",
    state$: component => component.canAdd$.pipe(map(canAdd => ({ primary: { disabled: !canAdd } }))),
    primaryAction: { label: () => "COMPONENTS.ADD_LOCATION.addLocation", run: component => component.add() },
    secondaryAction: { label: () => "COMPONENTS.ADD_LOCATION.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.addBeacon]: {
    load: () =>
      import("../../modals/add-beacon/add-beacon.module").then(m => ({
        component: m.AddBeaconComponent,
        module: m.AddBeaconComponentModule,
      })),
    size: "base",
    heading: () => "COMPONENTS.ADD_BEACON.header",
    state$: component => formValid$(component.form).pipe(map(valid => ({ primary: { disabled: !valid } }))),
    primaryAction: { label: () => "COMPONENTS.ADD_BEACON.addBeacon", run: component => component.add() },
    secondaryAction: { label: () => "COMPONENTS.ADD_BEACON.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.addNfcReader]: {
    load: () =>
      import("../../modals/add-nfc-reader/add-nfc-reader.module").then(m => ({
        component: m.AddNfcReaderComponent,
        module: m.AddNfcReaderComponentModule,
      })),
    size: "base",
    heading: () => "COMPONENTS.ADD_NFC_READER.header",
    state$: component => formValid$(component.form).pipe(map(valid => ({ primary: { disabled: !valid } }))),
    primaryAction: { label: () => "COMPONENTS.ADD_NFC_READER.addNfcReader", run: component => component.add() },
    secondaryAction: { label: () => "COMPONENTS.ADD_NFC_READER.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.linkPos]: {
    load: () =>
      import("../../modals/link-pos/link-pos.module").then(m => ({ component: m.LinkPosComponent, module: m.LinkPosComponentModule })),
    size: "base",
    heading: () => "COMPONENTS.LINK_POS.header",
    state$: component => component.selectedPos$.pipe(map(posId => ({ primary: { disabled: !posId } }))),
    primaryAction: { label: () => "COMPONENTS.LINK_POS.add", run: component => component.add() },
    secondaryAction: { label: () => "COMPONENTS.LINK_POS.close", run: component => component.dialogRef.close() },
  },
  [AppDialogs.allowedDomain]: {
    load: () =>
      import("../../modals/allowed-domain-dialog/allowed-domain-dialog.module").then(m => ({
        component: m.AllowedDomainDialogComponent,
        module: m.AllowedDomainDialogComponentModule,
      })),
    size: "base",
    heading: () => "ALLOWED_DOMAIN_DIALOG.title",
    state$: component => component.canSave$.pipe(map(canSave => ({ primary: { disabled: !canSave } }))),
    primaryAction: {
      label: data => (data.id ? "ALLOWED_DOMAIN_DIALOG.updateDomain" : "ALLOWED_DOMAIN_DIALOG.addDomain"),
      run: component => component.save(),
    },
    secondaryAction: { label: () => "COMPONENTS.CONFIRMATION.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.generateApiKey]: {
    load: () =>
      import("../../modals/generate-api-key/generate-api-key.module").then(m => ({
        component: m.GenerateApiKeyComponent,
        module: m.GenerateApiKeyComponentModule,
      })),
    size: "base",
    heading: () => "COMPONENTS.GENERATE_API_KEY.title",
    // The API key page creates the key: this only shows its value, once
    primaryAction: { label: () => "COMPONENTS.GENERATE_API_KEY.close", run: component => component.dialogRef.close() },
  },
  [AppDialogs.updateBalance]: {
    load: () =>
      import("../../modals/update-balance/update-balance.module").then(m => ({
        component: m.UpdateBalanceComponent,
        module: m.UpdateBalanceComponentModule,
      })),
    size: "small",
    heading: () => "UPDATE_BALANCE_PAGE.title",
    // Stays open while the points are booked
    state$: component =>
      combineLatest([component.canUpdate$, component.loading$]).pipe(
        map(([canUpdate, loading]) => ({ primary: { disabled: !canUpdate || loading, loading }, secondary: { disabled: loading } })),
      ),
    primaryAction: { label: () => "UPDATE_BALANCE_PAGE.updateBalance", run: component => component.updateBalance() },
    secondaryAction: { label: () => "UPDATE_BALANCE_PAGE.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.sendPush]: {
    load: () =>
      import("../../modals/send-push/send-push.module").then(m => ({ component: m.SendPushComponent, module: m.SendPushComponentModule })),
    size: "base",
    heading: () => "SEND_PUSH_PAGE.title",
    // Stays open while the push is sent
    state$: component =>
      combineLatest([formValid$(component.form), component.loading$]).pipe(
        map(([valid, loading]) => ({ primary: { disabled: !valid || loading, loading }, secondary: { disabled: loading } })),
      ),
    // A customer who can't receive pushes only gets the reason and a Dismiss, as in the dialog
    primaryAction: {
      label: () => "SEND_PUSH_PAGE.sendPush",
      visible: data => canReceivePush(data.customer),
      run: component => component.sendPush(),
    },
    secondaryAction: {
      label: data => (canReceivePush(data.customer) ? "SEND_PUSH_PAGE.cancel" : "SEND_PUSH_PAGE.dismiss"),
      run: component => component.dialogRef.close(),
    },
  },
  [AppDialogs.exportCouponCodes]: {
    load: () =>
      import("../../modals/export-coupon-codes/export-coupon-codes.module").then(m => ({
        component: m.ExportCouponCodesComponent,
        module: m.ExportCouponCodesComponentModule,
      })),
    size: "base",
    heading: () => "EXPORT_COUPON_CODES_PAGE.title",
    state$: component =>
      combineLatest([formValid$(component.numberOfCodes), component.loading$]).pipe(
        map(([valid, loading]) => ({ primary: { disabled: !valid || loading, loading } })),
      ),
    primaryAction: { label: () => "EXPORT_COUPON_CODES_PAGE.download", run: component => component.download() },
    secondaryAction: { label: () => "ADD_LINK_PAGE.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.cappedAmountWarning]: {
    load: () =>
      import("../../modals/capped-amount-warning/capped-amount-warning.module").then(m => ({
        component: m.CappedAmountWarningComponent,
        module: m.CappedAmountWarningComponentModule,
      })),
    size: "base",
    heading: data =>
      data.subscription.isApproachingCappedAmount
        ? "COMPONENTS.CAPPED_AMOUNT_WARNING.title"
        : "COMPONENTS.CAPPED_AMOUNT_WARNING.raiseTitle",
    state$: component => formValid$(component.form).pipe(map(valid => ({ primary: { disabled: !valid } }))),
    // At the plan's ceiling there is nothing to raise: the dialog explains it and only offers Not now
    primaryAction: {
      label: () => "COMPONENTS.CAPPED_AMOUNT_WARNING.increase",
      visible: data => canRaiseCappedAmount(data.subscription),
      run: component => component.confirm(),
    },
    secondaryAction: { label: () => "COMPONENTS.CAPPED_AMOUNT_WARNING.dismiss", run: component => component.dialogRef.close() },
  },
  [AppDialogs.qrWebApp]: {
    load: () =>
      import("../../modals/qr-web-app/qr-web-app.module").then(m => ({
        component: m.QRWebAppComponent,
        module: m.QRWebAppComponentModule,
      })),
    size: "small",
    heading: () => "QR_WEB_APP.title",
  },
  [AppDialogs.distribution]: {
    load: () =>
      import("../../modals/distribution-dialog/distribution-dialog.module").then(m => ({
        component: m.DistributionDialogComponent,
        module: m.DistributionDialogModule,
      })),
    size: "base",
    heading: data => `DISTRIBUTION_DIALOG.title.${data.type}`,
    secondaryAction: { label: () => "DISTRIBUTION_DIALOG.close", run: component => component.dialogRef.close() },
  },
  [AppDialogs.walletPassUpdate]: {
    load: () =>
      import("../../modals/wallet-pass-update/wallet-pass-update.module").then(m => ({
        component: m.WalletPassUpdateComponent,
        module: m.WalletPassUpdateComponentModule,
      })),
    size: "base",
    heading: () => "WALLET_PASS_UPDATE.title",
    state$: component =>
      combineLatest([component.canSend$, component.loading$]).pipe(
        map(([canSend, loading]) => ({ primary: { disabled: !canSend || loading, loading } })),
      ),
    primaryAction: { label: () => "WALLET_PASS_UPDATE.send", run: component => component.sendNotification() },
    secondaryAction: { label: () => "WALLET_PASS_UPDATE.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.addLink]: {
    load: () =>
      import("../../modals/add-link/add-link.module").then((m) => ({ component: m.AddLinkComponent, module: m.AddLinkComponentModule })),
    size: "base",
    heading: () => "ADD_LINK_PAGE.title",
    primaryAction: {
      label: (data) => (data.currentLinkUrl ? "ADD_LINK_PAGE.editLink" : "ADD_LINK_PAGE.addLink"),
      run: (component) => component.addLink(),
    },
    secondaryAction: {
      label: () => "ADD_LINK_PAGE.cancel",
      run: (component) => component.dialogRef.close(),
    },
  },
  // Pick, or create, where a profile form question saves its answers; picking a row closes it with the choice
  [AppDialogs.profileFormTarget]: {
    load: () =>
      import("../../modals/profile-form-target/profile-form-target.module").then(m => ({
        component: m.ProfileFormTargetComponent,
        module: m.ProfileFormTargetComponentModule,
      })),
    size: "base",
    heading: () => "COMPLETE_USER_PROFILE_PAGE.form.target.dialog.title",
    state$: component =>
      combineLatest([component.step$, component.creating$]).pipe(
        map(([step, creating]) =>
          step === "pick"
            ? { primary: { hidden: true } }
            : {
                heading: "COMPLETE_USER_PROFILE_PAGE.form.target.create.title",
                primary: { disabled: creating, loading: creating },
                secondary: { label: "COMPLETE_USER_PROFILE_PAGE.form.target.dialog.back", disabled: creating },
              },
        ),
      ),
    primaryAction: { label: () => "COMPLETE_USER_PROFILE_PAGE.form.target.create.submit", run: component => component.submitCreate() },
    secondaryAction: {
      label: () => "COMPLETE_USER_PROFILE_PAGE.form.target.create.cancel",
      run: component => (component.step$.value === "create" ? component.back() : component.dialogRef.close()),
    },
  },
  [AppDialogs.loyaltyLionApiKey]: {
    load: () =>
      import("../../modals/loyalty-lion-api-key/loyalty-lion-api-key.module").then(m => ({
        component: m.LoyaltyLionApiKeyComponent,
        module: m.LoyaltyLionApiKeyComponentModule,
      })),
    size: "base",
    heading: () => "LOYALTY_LION_API_KEY_DIALOG.title",
    primaryAction: { label: () => "LOYALTY_LION_API_KEY_DIALOG.openSettings", run: component => component.openSettings() },
    secondaryAction: { label: () => "LOYALTY_LION_API_KEY_DIALOG.dismiss", run: component => component.dialogRef.close() },
  },
  // The loyalty card editor's settings: Save and Cancel are Shopify's buttons; a dialog's other action (Restore, Remove)
  // is its secondary one, Shopify's close button cancelling
  [AppDialogs.walletText]: {
    load: () =>
      import("../../modals/wallet-text/wallet-text.module").then(m => ({ component: m.WalletTextComponent, module: m.WalletTextModule })),
    size: "base",
    heading: data => data.title,
    state$: component =>
      stateOf$([component.form.valueChanges, component.form.statusChanges, component.shownControl.valueChanges], () => ({
        primary: { disabled: component.form.invalid || component.unchanged },
      })),
    primaryAction: { label: () => "WALLET_TEXT_PAGE.save", run: component => component.save() },
    secondaryAction: { label: () => "WALLET_TEXT_PAGE.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.walletAdvanced]: {
    load: () =>
      import("../../modals/wallet-advanced/wallet-advanced.module").then(m => ({
        component: m.WalletAdvancedComponent,
        module: m.WalletAdvancedModule,
      })),
    size: "base",
    heading: () => "WALLET_ADVANCED_PAGE.title",
    state$: component =>
      stateOf$([component.posterModeControl.valueChanges, ...(component.actionsForm ? [component.actionsForm.valueChanges] : [])], () => ({
        primary: { disabled: !component.appleLayoutChanged && !component.actionsForm?.dirty },
      })),
    primaryAction: { label: () => "WALLET_ADVANCED_PAGE.save", run: component => component.save() },
    secondaryAction: { label: () => "WALLET_ADVANCED_PAGE.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.walletAction]: {
    load: () =>
      import("../../modals/wallet-action/wallet-action.module").then(m => ({
        component: m.WalletActionComponent,
        module: m.WalletActionModule,
      })),
    size: "base",
    heading: data => (data.position === "primary" ? "WALLET_ACTION_PAGE.titlePrimary" : "WALLET_ACTION_PAGE.titleSecondary"),
    state$: component => formValid$(component.form).pipe(map(valid => ({ primary: { disabled: !valid } }))),
    primaryAction: { label: () => "WALLET_ACTION_PAGE.save", run: component => component.save() },
    // Removing a set action; a new one has nothing to remove, and Shopify's close button cancels
    secondaryAction: {
      label: () => "WALLET_ACTION_PAGE.remove",
      visible: data => !!data.action,
      tone: () => "critical",
      run: component => component.remove(),
    },
  },
  [AppDialogs.walletColors]: {
    load: () =>
      import("../../modals/wallet-colors/wallet-colors.module").then(m => ({
        component: m.WalletColorsComponent,
        module: m.WalletColorsModule,
      })),
    size: "base",
    heading: () => "WALLET_COLORS_PAGE.title",
    state$: component => formValid$(component.form).pipe(map(valid => ({ primary: { disabled: !valid } }))),
    primaryAction: { label: () => "WALLET_COLORS_PAGE.save", run: component => component.save() },
    secondaryAction: { label: () => "WALLET_COLORS_PAGE.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.walletField]: {
    load: () =>
      import("../../modals/wallet-field/wallet-field.module").then(m => ({
        component: m.WalletFieldComponent,
        module: m.WalletFieldModule,
      })),
    size: "base",
    heading: data => data.title,
    state$: component =>
      stateOf$([component.form.valueChanges, component.shownControl.valueChanges, ...(component.translationForm ? [component.translationForm.valueChanges] : [])], () => ({
        primary: { disabled: component.unchanged },
        secondary: { disabled: !component.shownControl.value },
      })),
    primaryAction: { label: () => "WALLET_TEXT_PAGE.save", run: component => component.save() },
    // Back to the built-in texts, in the program's language only; Shopify's close button cancels
    secondaryAction: { label: () => "WALLET_FIELD_PAGE.restore", visible: data => !data.selectedLocale, run: component => component.restore() },
  },
  [AppDialogs.walletFields]: {
    load: () =>
      import("../../modals/wallet-fields/wallet-fields.module").then(m => ({
        component: m.WalletFieldsComponent,
        module: m.WalletFieldsModule,
      })),
    size: "base",
    heading: () => "WALLET_FIELDS.title",
    // Focus the dialog, as Polaris' Modal, not its first control (the close button)
    matDialogConfig: { autoFocus: "dialog" },
    state$: component => stateOf$([component.changes$], () => ({ primary: { disabled: !component.changed } })),
    primaryAction: { label: () => "WALLET_TEXT_PAGE.save", run: component => component.save() },
    secondaryAction: { label: () => "WALLET_TEXT_PAGE.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.walletSection]: {
    load: () =>
      import("../../modals/wallet-section/wallet-section.module").then(m => ({
        component: m.WalletSectionComponent,
        module: m.WalletSectionModule,
      })),
    size: "base",
    heading: () => "WALLET_SECTION_PAGE.title",
    primaryAction: { label: () => "WALLET_SECTION_PAGE.save", run: component => component.save() },
    secondaryAction: { label: () => "WALLET_SECTION_PAGE.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.walletAsset]: {
    load: () =>
      import("../../modals/wallet-asset/wallet-asset.module").then(m => ({
        component: m.WalletAssetComponent,
        module: m.WalletAssetModule,
      })),
    size: "base",
    heading: data => data.title ?? data.label,
    state$: component => stateOf$([component.changes$], () => ({ primary: { disabled: !component.changed } })),
    primaryAction: { label: () => "WALLET_ASSET_PAGE.save", run: component => component.save() },
    secondaryAction: { label: () => "WALLET_ASSET_PAGE.cancel", run: component => component.dialogRef.close() },
  },
  [AppDialogs.confirm]: {
    load: () => import("../../modals/confirm/confirm.module").then((m) => ({ component: m.ConfirmComponent })),
    size: "small",
    heading: (data) => data.title,
    primaryAction: {
      label: (data) => data.confirmText || "COMPONENTS.CONFIRMATION.confirm",
      tone: (data) => (data.buttonColor === "danger" ? "critical" : "default"),
      run: (component) => component.confirm(true),
    },
    secondaryAction: {
      label: (data) => data.cancelText || "COMPONENTS.CONFIRMATION.cancel",
      run: (component) => component.confirm(false),
    },
  },
};
